// AvatarCreator — AI-powered avatar creation pipeline
// Step 1: Build character prompt → Step 2: Generate T-pose image (Muse, Nano Banana 2 fallback)
// Step 3: Preview & approve → Step 4: Tripo 3D + rig on the server (buildAvatarFromImage)
import React, { useState, useRef, useCallback, useMemo, Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment, useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { X, Upload, Camera, Sparkles, Loader, CheckCircle, AlertCircle, Wand2, RefreshCw, ArrowRight, ArrowLeft, RotateCcw } from 'lucide-react';
import { putObject } from '../utils/uploadToStorage';
import { generateCharacterImage, buildCharacterPrompt, enhancePrompt } from '../services/openRouterApi';
import { useWorldStore } from '../stores/worldStore';
import { setActiveAvatar, updateAvatar, type Avatar } from '../services/avatarService';
import { captureModelThumbnail } from '../utils/captureModelThumbnail';
import { buildAvatarFromImage } from '../utils/buildAvatarFromImage';
import { inspectCreatedAvatar } from '../services/inspectCreatedAvatar';
import type { AvatarReadiness } from '../avatar/readiness';
import './AvatarCreator.css';

// ---- Live rotating 3D preview of the finished avatar ----
const SpinningAvatar: React.FC<{ url: string }> = ({ url }) => {
  const { scene } = useGLTF(url);
  const groupRef = useRef<THREE.Group>(null);

  const wrapper = useMemo(() => {
    // Clone (skinned-mesh safe) so we don't steal the cached scene from the
    // world avatar that loads the same URL after this preview.
    const model = cloneSkeleton(scene);
    // Ground-align: drop so the lowest vertex sits on y=0
    let minY = Infinity;
    model.traverse((child: any) => {
      if ((child.isMesh || child.isSkinnedMesh) && child.geometry?.attributes?.position) {
        const pos = child.geometry.attributes.position;
        for (let i = 0; i < pos.count; i++) { const y = pos.getY(i); if (y < minY) minY = y; }
      }
    });
    const g = new THREE.Group();
    g.add(model);
    if (isFinite(minY)) g.position.y = -minY;
    return g;
  }, [scene]);

  // Slow auto-rotation; user can still drag via OrbitControls
  React.useEffect(() => {
    let raf = 0;
    const tick = () => { if (groupRef.current) groupRef.current.rotation.y += 0.008; raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return <group ref={groupRef}><primitive object={wrapper} /></group>;
};

const AvatarPreview3D: React.FC<{ url: string }> = ({ url }) => (
  <div className="ac-preview3d">
    <Canvas camera={{ position: [0, 0.9, 2.6], fov: 42 }} dpr={[1, 1.5]}>
      <ambientLight intensity={0.7} />
      <directionalLight position={[3, 5, 2]} intensity={1.1} />
      <Suspense fallback={null}>
        <SpinningAvatar url={url} />
        <Environment files="/hdri/potsdamer_platz_1k.hdr" />
      </Suspense>
      <OrbitControls enablePan={false} enableZoom={false} minPolarAngle={Math.PI / 3} maxPolarAngle={Math.PI / 1.8} target={[0, 0.9, 0]} />
    </Canvas>
    <span className="ac-preview3d-hint">drag to rotate</span>
  </div>
);

interface AvatarCreatorProps {
  onAvatarCreated: (avatarUrl: string) => void;
  onClose: () => void;
  /** Render inline (the /welcome page) instead of as a full-screen modal. */
  embedded?: boolean;
  /**
   * Runs before the paid 3D build. Return false to stop — /welcome uses it to
   * show the code / Starter gate first, so nobody hits a quota error after
   * already approving their character.
   */
  beforeBuild?: () => Promise<boolean>;
  /**
   * Offer "Keep in wardrobe" next to "Use This Avatar" on the review step
   * (making a second avatar shouldn't force a switch). Called with the saved row.
   */
  onKept?: (avatar: Avatar) => void;
  /** Called with the saved row when the user equips it (before onAvatarCreated). */
  onEquipped?: (avatar: Avatar) => void;
}

type Step = 'design' | 'generating-image' | 'preview' | 'converting' | 'review' | 'done' | 'error';

// Prompt builder options
const STYLES = ['Realistic', 'Stylized', 'Cartoon', 'Anime', 'Low Poly', 'Sci-Fi', 'Fantasy'];
const BODY_TYPES = ['Average', 'Athletic', 'Slim', 'Stocky', 'Tall', 'Petite'];
const GENDERS = ['Male', 'Female', 'Androgynous'];
const SKIN_TONES = ['Light', 'Fair', 'Medium', 'Olive', 'Tan', 'Brown', 'Dark'];
const HAIR_STYLES = ['Short', 'Long', 'Bald', 'Ponytail', 'Mohawk', 'Curly', 'Braids', 'Buzz Cut', 'Afro', 'Bob'];
const HAIR_COLORS = ['Black', 'Brown', 'Blonde', 'Red', 'White', 'Blue', 'Pink', 'Purple', 'Green'];
const CLOTHING = ['Casual T-Shirt & Jeans', 'Casual Dress', 'Pajamas', 'Ladies Suit', 'Business Suit', 'Sporty Tracksuit', 'Summer Sundress', 'Leather Jacket & Jeans', 'Scrubs', 'Fantasy Armor', 'Futuristic Suit', 'Streetwear Hoodie', 'Medieval Tunic', 'Lab Coat', 'Military Gear', 'Superhero Costume', 'Cocktail Dress', 'Yoga Outfit', 'Chef Uniform', 'Kimono'];

// Swatch color maps for the color-picker option groups (keys must match the option strings above)
const SKIN_SWATCHES: Record<string, string> = {
  Light: '#F5D9BD', Fair: '#F0C9A8', Medium: '#D9A877', Olive: '#C19A6B', Tan: '#A67B5B', Brown: '#8D5524', Dark: '#5C3A21',
};
const HAIR_SWATCHES: Record<string, string> = {
  Black: '#1A1A1A', Brown: '#5A3825', Blonde: '#E6BE8A', Red: '#8C3B2E', White: '#E8E8E8', Blue: '#3B6EA5', Pink: '#E59BB5', Purple: '#7B5EA7', Green: '#4A7C59',
};

const AvatarCreator: React.FC<AvatarCreatorProps> = ({ onAvatarCreated, onClose, embedded, beforeBuild, onKept, onEquipped }) => {
  const [step, setStep] = useState<Step>('design');
  const [progress, setProgress] = useState(0);
  const [stageLabel, setStageLabel] = useState('');
  const [error, setError] = useState('');
  const [readiness, setReadiness] = useState<AvatarReadiness | null>(null);

  // Design state
  const [mode, setMode] = useState<'prompt' | 'photo'>('prompt');
  const [style, setStyle] = useState('Stylized');
  const [bodyType, setBodyType] = useState('Average');
  const [gender, setGender] = useState('Male');
  const [skinTone, setSkinTone] = useState('Medium');
  const [hairStyle, setHairStyle] = useState('Short');
  const [hairColor, setHairColor] = useState('Brown');
  const [clothing, setClothing] = useState('Casual T-Shirt & Jeans');
  const [extras, setExtras] = useState('');

  // Photo upload state
  const [uploadedPhoto, setUploadedPhoto] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Generated image preview
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  // S3 URL of the uploaded reference image (for Meshy)
  const [referenceImageUrl, setReferenceImageUrl] = useState<string | null>(null);
  // Finished rigged avatar GLB (for the live 3D review step)
  const [createdAvatarUrl, setCreatedAvatarUrl] = useState<string | null>(null);
  const draftAvatar = useRef<Avatar | null>(null);
  const [equipping, setEquipping] = useState(false);
  const [enhancing, setEnhancing] = useState(false);

  // Prompt assistance — expand the "extras" details into a richer description
  const handleEnhanceExtras = useCallback(async () => {
    if (!extras.trim() || enhancing) return;
    setEnhancing(true);
    try {
      const improved = await enhancePrompt(extras, 'character');
      setExtras(improved);
    } finally {
      setEnhancing(false);
    }
  }, [extras, enhancing]);

  const currentPrompt = useCallback(() => {
    return buildCharacterPrompt({ style, bodyType, gender, skinTone, hairStyle, hairColor, clothing, extras });
  }, [style, bodyType, gender, skinTone, hairStyle, hairColor, clothing, extras]);

  // ---- S3 helpers ----
  const uploadToS3 = async (data: Uint8Array | Blob, key: string, contentType: string): Promise<string> => {
    return putObject(key, data, contentType);
  };

  // ---- Photo handling ----
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => setUploadedPhoto(reader.result as string);
    reader.readAsDataURL(file);
  };

  // ---- Step 1→2: Generate T-pose image from prompt ----
  const handleGenerateImage = async () => {
    draftAvatar.current = null;
    try {
      setStep('generating-image');
      setStageLabel('Creating your character image...');
      setError('');
      setProgress(0);

      const result = await generateCharacterImage(currentPrompt());
      setGeneratedImage(result.dataUrl);

      // Upload the generated image to S3 so Meshy can access it
      const base64 = result.dataUrl.split(',')[1];
      const binary = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      const s3Url = await uploadToS3(binary, `avatars/references/${Date.now()}-tpose.png`, 'image/png');
      setReferenceImageUrl(s3Url);

      setStep('preview');
    } catch (err: any) {
      console.error('[AvatarCreator] image generation error:', err);
      setStep('error');
      setError(err.message || 'Image generation failed');
    }
  };

  // ---- Step 1→2 (photo mode): Upload photo and go to preview ----
  const handlePhotoUpload = async () => {
    draftAvatar.current = null;
    if (!uploadedPhoto) return;
    try {
      setStep('generating-image');
      setStageLabel('Uploading photo...');
      setError('');

      const base64 = uploadedPhoto.split(',')[1];
      const binary = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      const ext = uploadedPhoto.includes('image/png') ? 'png' : 'jpg';
      const s3Url = await uploadToS3(binary, `avatars/references/${Date.now()}-photo.${ext}`, `image/${ext}`);
      setReferenceImageUrl(s3Url);
      setGeneratedImage(uploadedPhoto);
      setStep('preview');
    } catch (err: any) {
      console.error('[AvatarCreator] photo upload error:', err);
      setStep('error');
      setError(err.message || 'Photo upload failed');
    }
  };

  // ---- Step 3→4: Build the approved image into a rigged 3D avatar (Tripo, server-side) ----
  const handleApproveAndConvert = async () => {
    if (!referenceImageUrl) return;
    if (beforeBuild && !draftAvatar.current && !(await beforeBuild())) return;
    try {
      setError('');
      setStep('converting');
      setReadiness(null);
      setStageLabel('Creating your 3D model…');
      setProgress(0);

      // Saved to the wardrobe unequipped: review must not replace the current avatar.
      const avatar = draftAvatar.current ?? await buildAvatarFromImage({
        imageDataUrl: referenceImageUrl,
        name: `Avatar ${new Date().toLocaleDateString()}`,
        creationParams: mode === 'prompt' ? { style, bodyType, gender, skinTone, hairStyle, hairColor, clothing, extras } : { mode: 'photo' },
        setActive: false,
        progress: { onStage: setStageLabel, onProgress: (p) => setProgress(Math.min(p, 96)) },
      });
      draftAvatar.current = avatar;

      setStageLabel('Saving…');
      let checked: AvatarReadiness | null = null;
      try { checked = await inspectCreatedAvatar(avatar.model_url); }
      catch (err) { console.warn('[AvatarCreator] Model checks unavailable:', err); }
      setReadiness(checked);

      // Tripo's own render is the thumbnail; render one from the model if it's missing.
      if (!avatar.thumbnail_url) {
        try {
          const thumbBlob = await captureModelThumbnail(avatar.model_url);
          if (thumbBlob) {
            const thumbUrl = await uploadToS3(thumbBlob, `avatars/thumbnails/${Date.now()}-thumb.png`, 'image/png');
            await updateAvatar(avatar.id, { thumbnail_url: thumbUrl });
            avatar.thumbnail_url = thumbUrl;
          }
        } catch (thumbErr) {
          console.warn('[AvatarCreator] Thumbnail capture failed:', thumbErr);
        }
      }

      setCreatedAvatarUrl(avatar.model_url);
      setProgress(100);
      setStep('review');
    } catch (err: any) {
      console.error('[AvatarCreator] conversion error:', err);
      setStep('error');
      setError(err.message || 'The build failed. Nothing was charged for a failed build — try again.');
    }
  };

  const handleEquip = async () => {
    if (!draftAvatar.current || equipping) return;
    setEquipping(true);
    setError('');
    try {
      await setActiveAvatar(draftAvatar.current.id);
      draftAvatar.current.is_active = true;
      useWorldStore.getState().setAvatarFromDb(draftAvatar.current);
      try {
        localStorage.setItem('eb-avatar-url', draftAvatar.current.model_url);
        localStorage.setItem('eb-avatar-animations', JSON.stringify(draftAvatar.current.animations || {}));
      } catch { /* storage unavailable */ }
      onEquipped?.(draftAvatar.current);
      setStep('done');
      onAvatarCreated(draftAvatar.current.model_url);
    } catch (err: any) {
      setError(err.message || 'Could not equip your avatar. Please try again.');
    } finally {
      setEquipping(false);
    }
  };

  const isProcessing = equipping || step === 'generating-image' || step === 'converting';

  return (
    <div className={embedded ? 'avatar-creator-embedded' : 'avatar-creator-overlay'}>
      <div className="avatar-creator-modal">
        {/* Header */}
        <div className="avatar-creator-header">
          <h2>
            {step === 'design' && 'Design Your Avatar'}
            {step === 'generating-image' && 'Creating Image...'}
            {step === 'preview' && 'Preview Your Character'}
            {step === 'converting' && 'Building 3D Avatar...'}
            {step === 'review' && 'Meet Your Avatar'}
            {step === 'done' && 'Avatar Ready!'}
            {step === 'error' && 'Something Went Wrong'}
          </h2>
          {!embedded && !isProcessing && step !== 'done' && (
            <button onClick={onClose} className="avatar-creator-close"><X size={22} /></button>
          )}
        </div>

        {/* Step indicator */}
        <div className="avatar-steps-bar">
          <StepDot num={1} label="Design" active={step === 'design'} done={step !== 'design'} />
          <div className="step-line" />
          <StepDot num={2} label="Preview" active={step === 'preview' || step === 'generating-image'} done={['converting', 'done'].includes(step)} />
          <div className="step-line" />
          <StepDot num={3} label="3D Avatar" active={step === 'converting' || step === 'review'} done={step === 'done'} />
        </div>

        <div className="avatar-creator-body">
          {/* ---- STEP 1: Design ---- */}
          {step === 'design' && (
            <>
              {/* Mode tabs */}
              <div className="avatar-creator-tabs">
                <button className={`avatar-tab ${mode === 'prompt' ? 'active' : ''}`} onClick={() => setMode('prompt')}>
                  <Wand2 size={16} /> AI Generate
                </button>
                <button className={`avatar-tab ${mode === 'photo' ? 'active' : ''}`} onClick={() => setMode('photo')}>
                  <Camera size={16} /> From Photo
                </button>
              </div>

              {mode === 'prompt' && (
                <div className="avatar-prompt-mode">
                  <div className="prompt-builder">
                    <section className="prompt-section">
                      <div className="prompt-section-head"><span className="prompt-section-title">Appearance</span></div>
                      <div className="prompt-row-2col">
                        <OptionGroup label="Style" options={STYLES} value={style} onChange={setStyle} />
                        <OptionGroup label="Gender" options={GENDERS} value={gender} onChange={setGender} />
                      </div>
                      <div className="prompt-row-2col">
                        <OptionGroup label="Body Type" options={BODY_TYPES} value={bodyType} onChange={setBodyType} />
                        <ColorOptionGroup label="Skin Tone" options={SKIN_TONES} value={skinTone} onChange={setSkinTone} swatches={SKIN_SWATCHES} />
                      </div>
                    </section>

                    <section className="prompt-section">
                      <div className="prompt-section-head"><span className="prompt-section-title">Hair</span></div>
                      <div className="prompt-row-2col">
                        <OptionGroup label="Hair Style" options={HAIR_STYLES} value={hairStyle} onChange={setHairStyle} />
                        <ColorOptionGroup label="Hair Color" options={HAIR_COLORS} value={hairColor} onChange={setHairColor} swatches={HAIR_SWATCHES} />
                      </div>
                    </section>

                    <section className="prompt-section">
                      <div className="prompt-section-head"><span className="prompt-section-title">Outfit</span></div>
                      <OptionGroup label="Clothing" options={CLOTHING} value={clothing} onChange={setClothing} className="option-group-clothing" />
                    </section>

                    <div className="prompt-custom">
                      <label>Additional details (optional)</label>
                      <div className="prompt-custom-wrap">
                        <textarea
                          value={extras}
                          onChange={(e) => setExtras(e.target.value)}
                          placeholder="e.g., blue eyes, scar on left cheek, cyberpunk visor, tattoos..."
                          rows={2}
                        />
                        <button className="prompt-enhance" onClick={handleEnhanceExtras} disabled={!extras.trim() || enhancing} title="Improve these details with AI">
                          {enhancing ? <Loader size={13} className="spinning" /> : <Wand2 size={13} />}
                          {enhancing ? 'Enhancing…' : 'Enhance'}
                        </button>
                      </div>
                    </div>

                    <div className="prompt-preview">
                      <label>Full prompt</label>
                      <p className="prompt-text">{currentPrompt()}</p>
                    </div>
                  </div>

                  <button className="avatar-btn-primary" onClick={handleGenerateImage}>
                    <Sparkles size={16} /> Generate T-Pose Image
                  </button>
                </div>
              )}

              {mode === 'photo' && (
                <div className="avatar-photo-mode">
                  <div className={`photo-drop-zone ${uploadedPhoto ? 'has-image' : ''}`} onClick={() => fileInputRef.current?.click()}>
                    {uploadedPhoto ? (
                      <img src={uploadedPhoto} alt="Preview" className="photo-preview" />
                    ) : (
                      <>
                        <Upload size={32} />
                        <p>Click to upload a photo</p>
                        <span>Clear front-facing photo works best</span>
                      </>
                    )}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} style={{ display: 'none' }} />
                  <div className="photo-tips">
                    <h4>Tips for best results:</h4>
                    <ul>
                      <li>Clear, well-lit photo</li>
                      <li>Front-facing, full body if possible</li>
                      <li>Plain background</li>
                    </ul>
                  </div>
                  <button className="avatar-btn-primary" disabled={!uploadedPhoto} onClick={handlePhotoUpload}>
                    <ArrowRight size={16} /> Continue with Photo
                  </button>
                </div>
              )}
            </>
          )}

          {/* ---- Generating image spinner ---- */}
          {step === 'generating-image' && (
            <div className="avatar-pipeline-status">
              <div className="pipeline-icon"><Loader size={48} className="spinning" /></div>
              <p className="pipeline-label">{stageLabel}</p>
              <p className="pipeline-sublabel">This usually takes 10-30 seconds</p>
            </div>
          )}

          {/* ---- STEP 2: Preview generated image ---- */}
          {step === 'preview' && generatedImage && (
            <div className="avatar-preview-step">
              <div className="preview-image-wrapper">
                <img src={generatedImage} alt="Generated character" className="preview-character-image" />
              </div>
              <p className="preview-caption">This is your character reference image. Happy with it?</p>
              <div className="preview-actions">
                <button className="avatar-btn-secondary" onClick={() => { setStep('design'); setGeneratedImage(null); setReferenceImageUrl(null); }}>
                  <ArrowLeft size={16} /> Go Back & Edit
                </button>
                <button className="avatar-btn-secondary" onClick={handleGenerateImage}>
                  <RefreshCw size={16} /> Regenerate
                </button>
                <button className="avatar-btn-primary" onClick={handleApproveAndConvert}>
                  <ArrowRight size={16} /> Convert to 3D Avatar
                </button>
              </div>
            </div>
          )}

          {/* ---- STEP 3: Converting to 3D ---- */}
          {step === 'converting' && (
            <div className="avatar-pipeline-status">
              <div className="pipeline-icon"><Loader size={48} className="spinning" /></div>
              <p className="pipeline-label">{stageLabel}</p>
              <div className="pipeline-progress-track">
                <div className="pipeline-progress-bar" style={{ width: `${progress}%` }} />
              </div>
              <span className="pipeline-percent">{progress}%</span>
              <div className="pipeline-stages">
                <StageIndicator label="3D Model" active={stageLabel.includes('3D')} done={stageLabel.includes('Rigging') || stageLabel.includes('Saving')} />
                <StageIndicator label="Rig" active={stageLabel.includes('Rigging')} done={stageLabel.includes('Saving')} />
                <StageIndicator label="Save" active={stageLabel.includes('Saving')} done={false} />
              </div>
              {generatedImage && (
                <div className="converting-thumbnail">
                  <img src={generatedImage} alt="Reference" />
                  <span>Reference image</span>
                </div>
              )}
            </div>
          )}

          {/* ---- STEP 3b: Live 3D review ---- */}
          {step === 'review' && createdAvatarUrl && (
            <div className="avatar-review-step">
              <AvatarPreview3D url={createdAvatarUrl} />
              <div className="avatar-readiness" role="status">
                <strong>Avatar checks</strong>
                {readiness ? <>
                  <p>Body rig: {readiness.body ? 'Detected — preview movement' : 'Needs review'}</p>
                  <p>Blinking: {readiness.blink ? 'Controls detected' : 'Needs facial preparation'}</p>
                  <p>Mouth movement: {readiness.mouth ? 'Controls detected' : 'Needs facial preparation'}</p>
                  {readiness.needsFacialPreparation && <p>This character keeps its appearance, but missing facial controls must be added before those features can work. Automatic facial preparation is not available yet.</p>}
                </> : <p>Checks could not finish. Preview this avatar before using it.</p>}
                <a href={`/avatar-sandbox?avatar=${encodeURIComponent(createdAvatarUrl)}`} target="_blank" rel="noopener noreferrer">Test movement and face</a>
              </div>
              <p role="alert">{error}</p>
              <p className="preview-caption">Your character has been created. Review its appearance and movement before using it.</p>
              <div className="preview-actions">
                <button className="avatar-btn-secondary" disabled={equipping} onClick={() => { draftAvatar.current = null; setStep('design'); setGeneratedImage(null); setReferenceImageUrl(null); setCreatedAvatarUrl(null); }}>
                  <RotateCcw size={16} /> Make another
                </button>
                {onKept && (
                  <button className="avatar-btn-secondary" disabled={equipping} onClick={() => draftAvatar.current && onKept(draftAvatar.current)}>
                    <CheckCircle size={16} /> Keep in wardrobe
                  </button>
                )}
                <button className="avatar-btn-primary" disabled={equipping} onClick={handleEquip}>
                  <CheckCircle size={16} /> Use This Avatar
                </button>
              </div>
            </div>
          )}

          {/* ---- Done ---- */}
          {step === 'done' && (
            <div className="avatar-pipeline-status">
              <div className="pipeline-icon"><CheckCircle size={48} className="text-success" /></div>
              <p className="pipeline-label">Avatar created!</p>
              <p className="pipeline-sublabel">Entering world...</p>
            </div>
          )}

          {/* ---- Error ---- */}
          {step === 'error' && (
            <div className="avatar-error">
              <AlertCircle size={40} />
              <p>{error}</p>
              {referenceImageUrl && generatedImage && (
                <button className="avatar-btn-primary" onClick={() => { setError(''); setStep('preview'); }}>Back to my picture</button>
              )}
              <button className="avatar-btn-secondary" onClick={() => { setStep('design'); setError(''); }}>
                Start Over
              </button>
            </div>
          )}
        </div>

        <div className="avatar-creator-footer">
          <p>Powered by Muse Image + Meshy + Tripo AI</p>
        </div>
      </div>
    </div>
  );
};

// ---- Sub-components ----

const StepDot: React.FC<{ num: number; label: string; active: boolean; done: boolean }> = ({ num, label, active, done }) => (
  <div className={`step-dot-wrapper ${active ? 'active' : ''} ${done ? 'done' : ''}`}>
    <div className="step-dot-circle">{done && !active ? '\u2713' : num}</div>
    <span className="step-dot-label">{label}</span>
  </div>
);

const StageIndicator: React.FC<{ label: string; active: boolean; done: boolean }> = ({ label, active, done }) => (
  <div className={`stage-indicator ${active ? 'active' : ''} ${done ? 'done' : ''}`}>
    <div className="stage-dot" />
    <span>{label}</span>
  </div>
);

const OptionGroup: React.FC<{ label: string; options: string[]; value: string; onChange: (v: string) => void; className?: string }> = ({ label, options, value, onChange, className }) => (
  <div className={`option-group${className ? ` ${className}` : ''}`}>
    <label>{label}</label>
    <div className="option-chips">
      {options.map((opt) => (
        <button key={opt} type="button" className={`option-chip ${value === opt ? 'selected' : ''}`} onClick={() => onChange(opt)} aria-pressed={value === opt}>
          {opt}
        </button>
      ))}
    </div>
  </div>
);

// Color-swatch variant of OptionGroup — used for Skin Tone & Hair Color.
// Wiring is identical to OptionGroup (still calls onChange with the option string).
const ColorOptionGroup: React.FC<{ label: string; options: string[]; value: string; onChange: (v: string) => void; swatches: Record<string, string> }> = ({ label, options, value, onChange, swatches }) => (
  <div className="option-group option-group-color">
    <label>{label}<span className="option-group-value">{value}</span></label>
    <div className="option-swatches">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          className={`option-swatch ${value === opt ? 'selected' : ''}`}
          onClick={() => onChange(opt)}
          title={opt}
          aria-label={opt}
          aria-pressed={value === opt}
        >
          <span className="option-swatch-dot" style={{ background: swatches[opt] || '#888' }} />
        </button>
      ))}
    </div>
  </div>
);

export default AvatarCreator;
